import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import FadeUp from '../components/FadeUp';
import ImagePlaceholder from '../components/ImagePlaceholder';

const heroWords = ['Spices', 'Crafted', 'with', 'Whanganui', 'Pride.'];

const features = [
  {
    icon: '🥬',
    title: 'Fresh Produce',
    desc: 'Hand-selected vegetables sourced fresh, delivered to our shelves daily for quality you can taste.',
  },
  {
    icon: '🌶️',
    title: 'Premium Spices',
    desc: 'An extensive range of aromatic spices and herbs from around the world, priced for every kitchen.',
  },
  {
    icon: '🌍',
    title: 'International Range',
    desc: 'Discover ingredients from South Asia, the Pacific, East Asia, the Middle East and beyond.',
  },
  {
    icon: '🏠',
    title: 'Locally Owned',
    desc: 'Proudly family-owned and operated right here in Whanganui, serving our community with heart.',
  },
];

const categories = [
  {
    name: 'Fresh Vegetables',
    desc: 'A daily selection of fresh, seasonal vegetables sourced for quality and flavour.',
    label: 'PHOTO: Fresh vegetables on display, colourful and neatly arranged',
  },
  {
    name: 'Spices and Herbs',
    desc: 'Hundreds of whole and ground spices, herbs and blends from around the globe.',
    label: 'PHOTO: Jars and packets of spices and herbs on store shelves',
  },
  {
    name: 'International Groceries',
    desc: 'Authentic ingredients from South Asian, Pacific, Asian and Middle Eastern cuisines.',
    label: 'PHOTO: International grocery products and sauces on shelves',
  },
  {
    name: 'Pantry Essentials',
    desc: 'Lentils, rice, flour, oils, canned goods and all the staples your pantry needs.',
    label: 'PHOTO: Pantry staples including rice, lentils and canned goods',
  },
];

const featuredProducts = [
  { name: 'Seasonal Vegetables', desc: 'Fresh, in-season produce selected for taste and value.', label: 'PHOTO: Featured seasonal vegetables display' },
  { name: 'Premium Spice Blends', desc: 'Ready-made blends for curries, marinades and rubs.', label: 'PHOTO: Premium spice blend packets and jars' },
  { name: 'International Sauces', desc: 'Authentic cooking sauces from across Asia and beyond.', label: 'PHOTO: International cooking sauces on shelf' },
  { name: 'Specialty Ingredients', desc: 'Hard-to-find items for traditional and contemporary recipes.', label: 'PHOTO: Specialty grocery ingredients on shelf' },
];

const testimonials = [
  {
    name: 'Priya S.',
    location: 'Whanganui',
    text: 'The freshest produce I have found in Whanganui. The vegetables are always vibrant and delicious, and the prices are unbeatable. A genuine gem for our family.',
  },
  {
    name: 'James T.',
    location: 'Whanganui',
    text: 'The spice range here is incredible. I found ingredients I could not get anywhere else in town. It is like having a world grocery store right on our doorstep.',
  },
  {
    name: 'Maria K.',
    location: 'Whanganui',
    text: 'Such a warm and welcoming store. The team is always helpful and friendly. I love supporting a local Whanganui business that takes such pride in what they do.',
  },
];

function TestimonialsSlider() {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(1);
  const timerRef = useRef(null);

  const go = (idx) => {
    setDirection(idx > active ? 1 : -1);
    setActive(idx);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setDirection(1);
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, []);

  const variants = {
    enter: { opacity: 0 },
    center: { opacity: 1, transition: { duration: 0.6, ease: 'easeInOut' } },
    exit: { opacity: 0, transition: { duration: 0.4 } },
  };

  const t = testimonials[active];

  return (
    <div style={{ textAlign: 'center', maxWidth: '680px', margin: '0 auto' }}>
      <div style={{ minHeight: '180px', position: 'relative', overflow: 'hidden' }}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={active}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <div style={{ fontSize: '28px', color: '#E67E22', marginBottom: '16px', letterSpacing: '4px' }}>
              &#9733;&#9733;&#9733;&#9733;&#9733;
            </div>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: 1.7, color: '#333', marginBottom: '20px', fontStyle: 'italic' }}>
              "{t.text}"
            </p>
            <p style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '15px', color: '#1A1A1A' }}>
              {t.name}
            </p>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#888', marginTop: '2px' }}>
              {t.location}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
        {testimonials.map((_, i) => (
          <button
            key={i}
            onClick={() => go(i)}
            aria-label={`Testimonial ${i + 1}`}
            style={{
              width: i === active ? '24px' : '8px',
              height: '8px',
              borderRadius: '100px',
              background: i === active ? '#C0392B' : '#ddd',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const catRef = useRef(null);
  const catInView = useInView(catRef, { once: true, margin: '-60px' });

  const styles = {
    page: { paddingTop: '68px' },

    heroSection: {
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'flex-end',
      padding: '0 0 64px',
      overflow: 'hidden',
    },
    heroBg: {
      position: 'absolute',
      inset: 0,
      objectFit: 'cover',
      width: '100%',
      height: '100%',
    },
    heroOverlay: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.1) 100%)',
    },
    heroContent: {
      position: 'relative',
      zIndex: 2,
      maxWidth: '1200px',
      width: '100%',
      margin: '0 auto',
      padding: '0 24px',
    },
    heroCard: {
      background: 'rgba(15,10,10,0.72)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '20px',
      padding: '40px 44px',
      maxWidth: '580px',
    },
    heroHeadline: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: 'clamp(36px, 5vw, 56px)',
      lineHeight: 1.15,
      color: '#FFFFFF',
      marginBottom: '16px',
    },
    heroSub: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '17px',
      lineHeight: 1.7,
      color: 'rgba(255,255,255,0.82)',
      marginBottom: '28px',
    },
    heroCtas: {
      display: 'flex',
      gap: '14px',
      flexWrap: 'wrap',
    },
    btnOrange: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: '#E67E22',
      color: '#1A1A1A',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 700,
      fontSize: '15px',
      padding: '14px 28px',
      borderRadius: '100px',
      textDecoration: 'none',
      transition: 'background 0.25s, transform 0.2s',
      border: 'none',
      cursor: 'pointer',
    },
    btnOutline: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: 'transparent',
      color: '#FFFFFF',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 700,
      fontSize: '15px',
      padding: '14px 28px',
      borderRadius: '100px',
      textDecoration: 'none',
      border: '2px solid rgba(255,255,255,0.6)',
      transition: 'border-color 0.25s, background 0.25s',
      cursor: 'pointer',
    },

    featuresSection: {
      background: '#1A1A1A',
      padding: '72px 24px',
    },
    featuresInner: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '36px',
    },
    featureItem: {
      textAlign: 'center',
    },
    featureIcon: {
      fontSize: '40px',
      marginBottom: '14px',
    },
    featureTitle: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 700,
      fontSize: '18px',
      color: '#FFFFFF',
      marginBottom: '8px',
    },
    featureDesc: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      color: 'rgba(255,255,255,0.65)',
      lineHeight: 1.65,
    },

    productsSection: {
      background: '#FFFFFF',
      padding: '88px 24px',
    },
    sectionInner: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    sectionLabel: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '13px',
      fontWeight: 600,
      color: '#E67E22',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      marginBottom: '8px',
    },
    sectionHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: 'clamp(28px, 4vw, 40px)',
      color: '#1A1A1A',
      marginBottom: '48px',
    },
    catGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '24px',
    },
    catCard: {
      background: '#FDF6EC',
      borderRadius: '16px',
      overflow: 'hidden',
      cursor: 'default',
      transition: 'transform 0.3s, box-shadow 0.3s',
      border: '1px solid #f0e8d8',
    },
    catCardImgWrap: {
      overflow: 'hidden',
    },
    catCardBody: {
      padding: '20px 22px 24px',
      position: 'relative',
    },
    catName: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 700,
      fontSize: '18px',
      color: '#1A1A1A',
      marginBottom: '6px',
    },
    catDesc: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      color: '#555',
      lineHeight: 1.6,
      marginBottom: '12px',
    },
    catBorder: {
      height: '3px',
      background: '#E67E22',
      borderRadius: '2px',
      width: '0',
      transition: 'width 0.4s ease',
    },

    featuredSection: {
      background: '#FDF6EC',
      padding: '88px 24px',
    },
    featuredGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '24px',
    },
    productCard: {
      background: '#FFFFFF',
      borderRadius: '16px',
      overflow: 'hidden',
      border: '1px solid rgba(0,0,0,0.07)',
      transition: 'transform 0.3s, box-shadow 0.3s',
    },
    productBody: {
      padding: '18px 20px 20px',
    },
    productName: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 700,
      fontSize: '16px',
      color: '#1A1A1A',
      marginBottom: '6px',
    },
    productDesc: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '13px',
      color: '#666',
      lineHeight: 1.6,
      marginBottom: '14px',
    },
    pill: {
      display: 'inline-block',
      background: 'rgba(230,126,34,0.12)',
      color: '#C0392B',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      fontSize: '12px',
      padding: '5px 12px',
      borderRadius: '100px',
    },

    testimonialsSection: {
      background: '#FFFFFF',
      padding: '88px 24px',
    },

    visitSection: {
      background: '#C0392B',
      padding: '80px 24px',
    },
    visitInner: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    visitHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: 'clamp(28px, 4vw, 42px)',
      color: '#FFFFFF',
      marginBottom: '8px',
    },
    visitSub: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '17px',
      color: 'rgba(255,255,255,0.8)',
      marginBottom: '32px',
    },
    visitGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '48px',
      alignItems: 'start',
    },
    visitDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    visitDetail: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    },
    visitIcon: {
      width: '44px',
      height: '44px',
      background: 'rgba(255,255,255,0.15)',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
      flexShrink: 0,
    },
    visitDetailText: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '16px',
      color: '#FFFFFF',
      fontWeight: 500,
    },
    visitDetailSub: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '13px',
      color: 'rgba(255,255,255,0.65)',
    },
    mapWrap: {
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
    },
  };

  return (
    <div style={styles.page}>
      {/* Hero */}
      <section style={styles.heroSection} aria-label="Hero">
        <img
          src="/images/store-interior.jpg"
          alt="Whanganui Spices and Veges store interior"
          style={styles.heroBg}
        />
        <div style={styles.heroOverlay} aria-hidden="true" />
        <div style={styles.heroContent}>
          <div style={styles.heroCard}>
            <h1 style={styles.heroHeadline}>
              {heroWords.map((word, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, ease: 'easeOut', delay: 0.1 + i * 0.1 }}
                  style={{
                    display: 'inline-block',
                    marginRight: word !== 'Pride.' ? '0.28em' : 0,
                    color: word === 'Whanganui' ? '#E67E22' : '#FFFFFF',
                  }}
                >
                  {word}
                </motion.span>
              ))}
            </h1>
            <motion.p
              style={styles.heroSub}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.7 }}
            >
              Your local destination for fresh vegetables, premium spices and international grocery products. Right here in Whanganui.
            </motion.p>
            <motion.div
              style={styles.heroCtas}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.85 }}
            >
              <Link
                to="/products"
                style={styles.btnOrange}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#d4711e'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#E67E22'; e.currentTarget.style.transform = 'scale(1)'; }}
              >
                Browse Products <span aria-hidden="true">&#8594;</span>
              </Link>
              <Link
                to="/contact"
                style={styles.btnOutline}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'white'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; }}
              >
                Find Us <span aria-hidden="true">&#8594;</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features strip */}
      <section style={styles.featuresSection} aria-label="Store highlights">
        <div style={styles.featuresInner}>
          {features.map((f, i) => (
            <FadeUp key={f.title} delay={i * 0.1}>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon} role="img" aria-label={f.title}>{f.icon}</div>
                <h3 style={styles.featureTitle}>{f.title}</h3>
                <p style={styles.featureDesc}>{f.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Products section */}
      <section style={styles.productsSection} aria-label="Product categories">
        <div style={styles.sectionInner}>
          <FadeUp>
            <p style={styles.sectionLabel}>Explore Our Range</p>
            <h2 style={styles.sectionHeading}>What We Stock</h2>
          </FadeUp>
          <div ref={catRef} style={styles.catGrid} className="cat-grid-responsive">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.name}
                style={styles.catCard}
                initial={{ opacity: 0, y: 40 }}
                animate={catInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, ease: 'easeOut', delay: i * 0.1 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.12)';
                  const bar = e.currentTarget.querySelector('.cat-border');
                  if (bar) bar.style.width = '100%';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                  const bar = e.currentTarget.querySelector('.cat-border');
                  if (bar) bar.style.width = '0';
                }}
              >
                <div style={styles.catCardImgWrap}>
                  <ImagePlaceholder label={cat.label} height={180} borderRadius="0" />
                </div>
                <div style={styles.catCardBody}>
                  <h3 style={styles.catName}>{cat.name}</h3>
                  <p style={styles.catDesc}>{cat.desc}</p>
                  <div className="cat-border" style={styles.catBorder} aria-hidden="true" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section style={styles.featuredSection} aria-label="Featured products">
        <div style={styles.sectionInner}>
          <FadeUp>
            <p style={styles.sectionLabel}>In Store Now</p>
            <h2 style={{ ...styles.sectionHeading, color: '#C0392B' }}>Fresh In Store</h2>
          </FadeUp>
          <div style={styles.featuredGrid}>
            {featuredProducts.map((p, i) => (
              <FadeUp key={p.name} delay={i * 0.1}>
                <div
                  style={styles.productCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <ImagePlaceholder label={p.label} height={160} borderRadius="16px 16px 0 0" />
                  <div style={styles.productBody}>
                    <h3 style={styles.productName}>{p.name}</h3>
                    <p style={styles.productDesc}>{p.desc}</p>
                    <span style={styles.pill}>Available In Store</span>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={styles.testimonialsSection} aria-label="Customer reviews">
        <div style={styles.sectionInner}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <p style={styles.sectionLabel}>Happy Customers</p>
              <h2 style={styles.sectionHeading}>What Whanganui Says</h2>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <TestimonialsSlider />
          </FadeUp>
        </div>
      </section>

      {/* Visit Us */}
      <section style={styles.visitSection} aria-label="Visit us in Whanganui">
        <div style={styles.visitInner}>
          <FadeUp>
            <h2 style={styles.visitHeading}>Come Visit Us in Whanganui.</h2>
            <p style={styles.visitSub}>We would love to see you in store. Find us in Whanganui, New Zealand.</p>
          </FadeUp>
          <div style={styles.visitGrid} className="visit-grid">
            <FadeUp delay={0.1}>
              <div style={styles.visitDetails}>
                <div style={styles.visitDetail}>
                  <div style={styles.visitIcon} aria-hidden="true">&#128205;</div>
                  <div>
                    <p style={styles.visitDetailText}>Whanganui, New Zealand</p>
                    <p style={styles.visitDetailSub}>Come visit us in store</p>
                  </div>
                </div>
                <div style={styles.visitDetail}>
                  <div style={styles.visitIcon} aria-hidden="true">&#128222;</div>
                  <div>
                    <a href="tel:0634844398" style={{ ...styles.visitDetailText, textDecoration: 'none' }}>06-348 4398</a>
                    <p style={styles.visitDetailSub}>Give us a call</p>
                  </div>
                </div>
                <div style={styles.visitDetail}>
                  <div style={styles.visitIcon} aria-hidden="true">&#9993;</div>
                  <div>
                    <a href="mailto:shiriwastowenterprises@gmail.com" style={{ ...styles.visitDetailText, textDecoration: 'none', wordBreak: 'break-all', fontSize: '14px' }}>
                      shiriwastowenterprises@gmail.com
                    </a>
                    <p style={styles.visitDetailSub}>Send us a message</p>
                  </div>
                </div>
                <div style={{ marginTop: '8px' }}>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: '#FFFFFF',
                      color: '#C0392B',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '15px',
                      padding: '14px 28px',
                      borderRadius: '100px',
                      textDecoration: 'none',
                      transition: 'background 0.2s, transform 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#FDF6EC'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    Get in Touch &#8594;
                  </Link>
                </div>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div style={styles.mapWrap}>
                <iframe
                  src="https://maps.google.com/maps?q=Whanganui+Spices+and+Veges+Whanganui+New+Zealand&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="400"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Whanganui Spices and Veges location map"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .visit-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .cat-grid-responsive { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
